import {action, makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {type IPlayersStore, type IPlayer, IPoolFilters} from "data/stores/players/players.store";
import {type ITeamStore} from "data/stores/team/team.store";
import {Bindings} from "data/constants/bindings";
// import React, {ChangeEvent} from "react";
import {MatchStatus, PlayerPosition, PlayerStatus, RoundStatus, SortOrder} from "data/enums";
import {chain, get} from "lodash";
// import {SelectChangeEvent} from "@mui/material";
import type {ISquad, ISquadsStore} from "data/stores/squads/squads.store";
import {type IRoundsStore} from "data/stores/rounds/rounds.store";
import {ISelectOption, type ITeamBuilderStore} from "data/stores/team_builder/team_builder.store";
import {SelectChangeEvent} from "@mui/material";
import {ChangeEvent, SyntheticEvent} from "react";
import {
	getPlayerPosition,
	getPlayerimage,
	handleNullValue,
	handleSortNullValue,
	isAnyTrue,
} from "data/utils/helpers";
import {currencyFormat} from "data/utils";
import {useNavigate} from "react-router-dom";

export interface IPlayerExtended extends IPlayer {
	isInTeam: boolean;
	canAddToTeam: boolean;
	isFavourite?: boolean;
	isLocked?: boolean;
	showAddButton?: boolean;
	playerImgPath?: string;
	displayStat?: number | string;
	showNoButton?: boolean;
	favouriteAction?: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	isTradeMode?: boolean;
	isInTrade?: boolean;
	removeTradeAction?: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	isPlayerTradeBE?: boolean;
	isPlayerIncomingTrade?: boolean;
	isPlayerOutgoingTrade?: boolean;
	isRoundOneComplete?: boolean;
}

export interface IStatsCenterController extends ViewController<IInit> {
	removeFromTeam: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	addToTeam: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	resetPosition: () => void;
	setPosition: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	updateFilter: (e: SelectChangeEvent | SelectChangeEvent<string>) => void;
	updateTeamFilter: (e: SelectChangeEvent | SelectChangeEvent<string>) => void;
	updateStatusFilter: (e: SelectChangeEvent | SelectChangeEvent<string | PlayerStatus>) => void;
	updateSearch: (e: ChangeEvent<HTMLInputElement>) => void;
	sortPlayers: () => void;
	resetFilters: () => void;
	resetSearch: () => void;
	setFavourite: () => void;
	isPositionFixed: boolean;
	setFavouritePlayer: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	removeFavouritePlayer: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	canAddPlayerToTeam: (player: IPlayer) => boolean;
	isPlayerLocked: (squadId: number) => boolean;
	addPlayerOutTrade: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	addPlayerInTrade: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	removePlayerOutTrade: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	removePlayerInTrade: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
	setPositionFixed: (value: boolean) => void;
	setStatOrderBy: (stat: string) => void;
	getPlayerStat: (stat: string, player: IPlayerExtended) => string | number;
	setTradeModeActive: () => void;
	toggleTotalPointsSort: () => void;
	toggleTotalPointsOff: () => void;
	isPlayerFavourite: (playerId: number) => boolean;
	get filters(): IPoolFilters;
	get isSortByTotal(): boolean;

	get players(): IPlayerExtended[];
	get isTradeMode(): boolean;
	get isTeamLoaded(): boolean;
	get order(): SortOrder;

	get squads(): ISquad[];

	get positionsArray(): PlayerPosition[];

	get statsOptions(): ISelectOption[];
	get priceOptions(): ISelectOption[];
	get statusOptions(): ISelectOption[];
	get favouritePlayers(): number[];
	get isCurrentRoundIndigenous(): boolean;
	get isTradeInDisabled(): boolean;
	get isTradeOutDisabled(): boolean;
	get isTeamFirstRoundComplete(): boolean;
}

interface IInit {
	navigate?: ReturnType<typeof useNavigate>;
	isMobile: boolean;
}

const DISPLAYINITIAL = "Average Pts";

@injectable()
export class StatsCenterController implements IStatsCenterController {
	private _navigate!: IInit["navigate"];
	private _isMobile: boolean = false;
	@observable protected _hasFetchedTeam: boolean = false;

	constructor(
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.TeamBuilderStore) private _teamBuilderStore: ITeamBuilderStore
	) {
		makeAutoObservable(this);
	}
	// {val: "", label: "", labelShort: ""}
	private _statsOptions = [
		{val: "gamesPlayed", label: "Games Played", labelShort: "GP"},
		{val: "cost", label: "$ Price", labelShort: "Price"},
		{val: "lastRoundPriceChange", label: "Round $ Change", labelShort: "RND $ Change"},
		{val: "seasonPriceChange", label: "Season $ Change", labelShort: "SEA $ Change"},
		{val: "costPerPoint", label: "$ per point", labelShort: "$ / Point"},
		{val: "selections", label: "% Selected", labelShort: "% Sel"},
		{val: "avgPoints", label: DISPLAYINITIAL, labelShort: "Avg"},
		{val: "avgPointsLast3", label: "Last 3 Average", labelShort: "L3 Avg"},
		this._isMobile
			? {val: "roundPoints", label: "Rnd Pts", labelShort: "Rnd"}
			: {val: "lastRound", label: "Last Rnd Pts", labelShort: "Last"},
		{val: "totalPoints", label: "Total Pts", labelShort: "Total"},
	];

	@observable private _isSortByTotal = false;

	get squads(): ISquad[] {
		return this._squadsStore.list;
	}

	get isSortByTotal() {
		return this._isSortByTotal;
	}
	get filters(): IPoolFilters {
		return this._teamBuilderStore.filters;
	}
	get searchFilter(): string {
		return this._teamBuilderStore.searchFilter;
	}

	get isTeamLoaded() {
		return this._teamStore.isTeamLoaded;
	}

	get isCurrentRoundIndigenous() {
		return this._roundsStore.isCurrentRoundIndigenous;
	}
	isPlayerFavourite = (playerId: number) => {
		return this._playersStore.favouritePlayers.includes(playerId);
	};

	get isPositionFixed() {
		return this._teamStore.isPositionFixed;
	}

	get statsOptions() {
		return [
			{val: "gamesPlayed", label: "Games Played", labelShort: "GP"},
			{val: "cost", label: "$ Price", labelShort: "Price"},
			{val: "lastRoundPriceChange", label: "Round $ Change", labelShort: "RND $ Change"},
			{val: "seasonPriceChange", label: "Season $ Change", labelShort: "SEA $ Change"},
			{val: "costPerPoint", label: "$ per point", labelShort: "$ / Point"},
			{val: "selections", label: "% Selected", labelShort: "% Sel"},
			{val: "avgPoints", label: DISPLAYINITIAL, labelShort: "Avg"},
			{val: "avgPointsLast3", label: "Last 3 Average", labelShort: "L3 Avg"},
			{val: "roundPoints", label: "Rnd Pts", labelShort: "Rnd"},
			{val: "totalPoints", label: "Total Pts", labelShort: "Total"},
		];
	}

	get priceOptions() {
		return this._teamBuilderStore.priceOptions;
	}
	get isCurrentRoundLive() {
		return this._roundsStore.isCurrentRoundLive;
	}

	get order() {
		return this._teamBuilderStore.order;
	}

	get isTradeMode() {
		return this._teamStore.isTradeMode;
	}

	get favouritePlayers() {
		return this._playersStore.favouritePlayers;
	}

	get statusOptions() {
		return this._teamBuilderStore.statusOptions;
	}

	get isTeamFirstRoundComplete() {
		return this._teamStore.isTeamStartRoundComplete;
	}

	get isTradeOutDisabled() {
		return this._teamStore.isTradeOutDisabled;
	}

	get isTradeInDisabled() {
		return this._teamStore.isTradeInDisabled;
	}

	get isRoundOneComplete() {
		return this._roundsStore.isRoundOneComplete;
	}

	get lastRoundId() {
		const currentRound = this._roundsStore.currentRound;
		const currentRoundId = currentRound?.id;
		if (currentRound?.status !== RoundStatus.Scheduled && currentRoundId) {
			return currentRoundId;
		}
		if (currentRoundId) {
			return currentRoundId - 1;
		}
		return 1;
	}

	get players(): IPlayerExtended[] {
		const teamIds = this._teamStore.teamPlayerIds;

		const maxPrice = this._teamStore.remainingSalary;

		const players = this._playersStore.getFilteredPlayers(
			this.filters,
			this.searchFilter,
			maxPrice
		);
		return chain(players)
			.map((player) => {
				const isInTeam = teamIds.includes(player.id);
				const canAddToTeam = this._teamStore.canAddPlayerToTeam(player);
				const isLocked = this.isPlayerLocked(player.squadId);
				const isFavourite = this.favouritePlayers.includes(player.id);
				const isInTrade = this.isPlayerInTrade(player.id);
				const isPlayerTradeBE = this.isPlayerTradeBE(player.id);
				const removeTradeAction =
					this.isPlayerInOrOutTrade(player.id) === "in"
						? this.removePlayerInTrade
						: this.removePlayerOutTrade;
				return {
					...player,
					isInTeam,
					isFavourite,
					isLocked: this.isPlayerLocked(player.squadId),
					playerImgPath: getPlayerimage(player.id, 500),
					displayStat: this.getPlayerSelectedStat(player),
					showNoButton: (!canAddToTeam && !isInTeam) || isLocked,
					stats: {
						...player.stats,
						nextOpponent: player.fixture.isPlayerHome
							? player.fixture.awaySquad?.id
							: player.fixture.homeSquad?.id,
					},
					favouriteAction: isFavourite
						? this.removeFavouritePlayer
						: this.setFavouritePlayer,
					canAddToTeam,
					showAddButton: !isInTeam && canAddToTeam,
					isTradeMode: this._teamStore.isTradeMode,
					isPlayerIncomingTrade: this.isPlayerInOrOutTrade(player.id) === "in",
					isPlayerOutgoingTrade: this.isPlayerInOrOutTrade(player.id) === "out",
					isInTrade,
					removeTradeAction,
					isPlayerTradeBE,
					isRoundOneComplete: this.isRoundOneComplete,
				};
			})
			.orderBy([this.getPlayerSelectedStatForFilter], [this.order, SortOrder.ASC])
			.value();
	}

	toggleTotalPointsSort = () => {
		this._isSortByTotal = true;
		this._teamBuilderStore.sortPlayers();
	};
	toggleTotalPointsOff = () => {
		this._isSortByTotal = false;
	};

	isPlayerInTrade = (playerId: number) => {
		const trades = this._teamStore.trades;
		let isInTrade = false;
		trades.forEach((trade) => {
			if ([trade.in, trade.out].includes(playerId)) {
				isInTrade = true;
			}
		});
		return isInTrade;
	};

	getPlayerCompareStat = (stat: string, player: IPlayerExtended) => {
		if (stat === "cost") {
			return player.cost;
		}
		if (stat === "selections") {
			return get(player, "selections", 0);
		}
		return get(player.stats, stat, 0);
	};

	get roundId() {
		return this._roundsStore.currentRound?.id || 1;
	}

	get filterRoundId() {
		const isCurrentRoundStarted =
			this._roundsStore.currentRound?.status !== RoundStatus.Scheduled;
		if (isCurrentRoundStarted && this._roundsStore.currentRound?.id === 1) {
			return 1;
		}

		return isCurrentRoundStarted && this.roundId !== 1 ? this.roundId : this.roundId - 1;
	}

	getPlayerSelectedStatForFilter = (player: IPlayer) => {
		const activeStat = this.filters.stat;

		if (activeStat === "cost") {
			return player.cost;
		}
		if (activeStat === "selections") {
			return handleNullValue(get(player, "selections", 0));
		}
		if (isAnyTrue([activeStat.includes("cost"), activeStat.includes("Price")])) {
			const val = get(player.stats, activeStat, 0);

			return handleNullValue(val);
		}

		if (isAnyTrue([activeStat === "roundPoints", activeStat === "lastRound"])) {
			return handleSortNullValue(get(player.stats.scores, this.filterRoundId, 0), true);
		}
		if (activeStat === "avgPointsLast3") {
			return handleSortNullValue(get(player.stats, "avgPointsLast3", 0), true);
		}
		if (activeStat === "avgPoints") {
			return handleSortNullValue(get(player.stats, "avgPoints", 0), true);
		}

		return handleSortNullValue(get(player.stats, activeStat, "-"), true);
	};

	getPlayerStat = (stat: string, player: IPlayerExtended) => {
		let value;

		if (stat === "cost") {
			value = currencyFormat({input: player.cost, showDecimal: true});
			return handleNullValue(value);
		}
		if (stat.includes("cost") || stat.includes("Price")) {
			const val = get(player.stats, stat, 0);
			value = currencyFormat({input: val, showDecimal: true});

			return handleNullValue(value);
		}
		if (stat === "selections") {
			value = get(player, "selections", "-");
			return handleNullValue(value);
		}
		if (stat === "lastRound" || stat === "roundPoints") {
			value = get(player.stats.scores, this.lastRoundId, "-");
			return handleNullValue(value);
		}
		value = get(player.stats, stat, "-");
		return handleNullValue(value);
	};

	isPlayerTradeBE = (playerId: number) => {
		const trades = this._teamStore.trades;
		const trade = trades.find((trade) => [trade.in, trade.out].includes(playerId));
		if (trade) {
			return trade.fromBE;
		}
		return false;
	};

	setPositionFixed = (value: boolean) => {
		this._teamStore.setPositionFixed(value);
	};

	isPlayerInOrOutTrade = (playerId: number) => {
		const trades = this._teamStore.trades;
		let inOrOut = "";
		trades.forEach((trade) => {
			if (trade.in === playerId) {
				inOrOut = "in";
			}
			if (trade.out === playerId) {
				inOrOut = "out";
			}
		});
		return inOrOut;
	};

	get positionsArray(): PlayerPosition[] {
		return this._teamStore.positionsArray;
	}

	isPlayerLocked = (squadId: number) => {
		const round = this._roundsStore.selectedRound;
		const playerMatch = round?.tournaments.find((match) =>
			[match.awaySquadId, match.homeSquadId].includes(squadId)
		);
		if (playerMatch) {
			return playerMatch.status !== MatchStatus.Scheduled;
		}
		return false;
	};

	getPlayerSelectedStat = (player: IPlayer) => {
		const activeStat = this.filters.stat;
		let selectedStat = get(player, activeStat, "-");
		if (activeStat === "cost") {
			const shouldShowDecimal = player.cost > 1000000;
			selectedStat = currencyFormat({
				input: Number(selectedStat),
				showDecimal: shouldShowDecimal,
			});
		}
		return selectedStat;
	};

	@action
	async checkFetchTeam(): Promise<void> {
		if (this._hasFetchedTeam) {
			return;
		}
		this._hasFetchedTeam = true;
		await this._teamStore.requestTeam();
	}

	@action
	removeFromTeam = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		this.addRemoveTeam(e.currentTarget.value, "removePlayerFromTeam");
	};

	@action
	addToTeam = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		this.addRemoveTeam(e.currentTarget.value, "addPlayerToTeam");
	};

	addRemoveTeam(strId: string, methodName: "removePlayerFromTeam" | "addPlayerToTeam") {
		const id = Number(strId);
		const player = this._playersStore.getPlayerById(id);

		if (player) {
			const position = getPlayerPosition(player);
			this._teamStore[methodName](id, position);
		}
	}

	sortPlayers = () => {
		this._teamBuilderStore.sortPlayers();
	};

	setStatOrderBy = (stat: string) => {
		this.toggleTotalPointsOff();
		this._teamBuilderStore.updateStatField(stat);
	};

	resetFilters = () => {
		this._teamBuilderStore.resetFilters();
	};
	resetSearch = () => {
		this._teamBuilderStore.resetSearch();
	};

	setFavourite = () => {
		this._teamBuilderStore.setFavourite();
	};

	setTradeModeActive = () => {
		this._teamStore.setTradeMode(true);
	};

	setFavouritePlayer = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
		const playerId = Number(e.currentTarget.value);
		await this._playersStore.setFavouritePlayer(playerId);
	};

	removeFavouritePlayer = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
		const playerId = Number(e.currentTarget.value);
		await this._playersStore.removeFavouritePlayer(playerId);
	};

	resetPosition = () => {
		this._teamBuilderStore.resetPosition();
	};

	setPosition = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		this._teamBuilderStore.setPosition(e);
	};
	updateFilter = (e: SelectChangeEvent | SelectChangeEvent<string[]>) => {
		this._teamBuilderStore.updateFilter(e);
	};
	updateSearch = (e: ChangeEvent<HTMLInputElement>) => {
		this._teamBuilderStore.updateSearch(e);
	};

	updateTeamFilter = (e: SelectChangeEvent | SelectChangeEvent<string[]>) => {
		this._teamBuilderStore.updateTeamFilter(e);
	};

	updateStatusFilter = (e: SelectChangeEvent | SelectChangeEvent<string | PlayerStatus>) => {
		this._teamBuilderStore.updateStatusFilter(e);
	};

	canAddPlayerToTeam = (player: IPlayer) => {
		return this._teamStore.canAddPlayerToTeam(player);
	};

	removePlayerOutTrade = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		this._teamStore.setTradeMode(true);
		const playerId = Number(e.currentTarget.value);
		this._teamStore.removePlayerOutTrade(playerId);
	};

	removePlayerInTrade = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		this._teamStore.setTradeMode(true);
		const playerId = Number(e.currentTarget.value);
		this._teamStore.removePlayerInTrade(playerId);
	};

	addPlayerInTrade = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		this._teamStore.setTradeMode(true);
		const playerId = Number(e.currentTarget.value);
		this._teamStore.addPlayerInTrade(playerId);
		this._navigate!(`/team?from=stats-center`);
	};

	addPlayerOutTrade = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		this._teamStore.setTradeMode(true);
		const playerId = Number(e.currentTarget.value);
		this._teamStore.addPlayerOutTrade(playerId);
		this._navigate!(`/team?from=stats-center`);
	};
	dispose(): void {
		return;
	}

	async init({navigate, isMobile}: IInit) {
		this._navigate = navigate;
		this._isMobile = isMobile;
		await this._roundsStore.fetchRounds();
		await this._squadsStore.fetchSquads();
		await this._playersStore.fetchPlayers();
		await this._playersStore.fetchFavouritePlayers();
		await this.checkFetchTeam();
		const isSeasonStarted =
			get(this._roundsStore.list, "[0].status", RoundStatus.Scheduled) !==
			RoundStatus.Scheduled;

		if (!isSeasonStarted) {
			this._teamBuilderStore.updateStatField("roundPoints");
		}
	}
}
